<script setup lang="ts">
  import { computed, inject, provide, useId } from 'vue';
  import { FontIcon } from '../FontIcon';
  import { attributeContextKey } from './Attribute.context';
  import AttributeInfo from './AttributeInfo.vue';
  import {
    attributeActionClass,
    attributeAlignClasses,
    attributeContainerClass,
    attributeDataVariants,
    attributeDetailClasses,
    attributeIconClass,
    attributeLabelClass,
    attributeLabelColumnClass,
    attributeLabelGroupClass,
    attributeLayoutClasses,
    attributeLayoutLabelClasses,
    attributeLegacyClass,
    attributeSectionClasses,
    attributeToneClasses,
    attributeValueGroupClass,
    attributeVerticalMiddleClass,
    attributeVerticalProminentDataClass,
  } from './Attribute.css';
  import {
    AttributeAlign,
    AttributeEmphasis,
    AttributeLayout,
    type AttributeProps,
    type AttributeSlots,
    AttributeTone,
  } from './Attribute.types';

  const props = withDefaults(defineProps<AttributeProps>(), {
    horizontal: false,
    tone: AttributeTone.Neutral,
    align: AttributeAlign.Center,
  });

  defineSlots<AttributeSlots>();

  const context = inject(attributeContextKey, null);

  provide(attributeContextKey, null);

  const uid = useId();
  const resolvedTestId = computed(() => props.testId ?? `ds-attribute-${uid}`);

  const resolvedLayout = computed(
    () =>
      props.layout ??
      (props.horizontal
        ? AttributeLayout.Horizontal
        : AttributeLayout.Vertical),
  );

  const resolvedEmphasis = computed(
    () => props.emphasis ?? props.variant ?? AttributeEmphasis.Default,
  );

  const isLegacy = computed(() => props.layout === undefined);

  const isVertical = computed(
    () => resolvedLayout.value === AttributeLayout.Vertical,
  );

  const labelTag = computed(() => (context ? 'dt' : 'span'));
  const valueTag = computed(() => (context ? 'dd' : 'div'));

  const rootClasses = computed(() => [
    attributeContainerClass,
    attributeLayoutClasses[resolvedLayout.value],
    isVertical.value ? undefined : attributeAlignClasses[props.align],
    { [attributeLegacyClass]: isLegacy.value },
    {
      [attributeVerticalMiddleClass]:
        !isLegacy.value &&
        isVertical.value &&
        resolvedEmphasis.value === AttributeEmphasis.Middle,
    },
  ]);

  const labelGroupClasses = computed(() => [
    attributeLabelGroupClass,
    isLegacy.value
      ? undefined
      : attributeLayoutLabelClasses[resolvedLayout.value],
    {
      [attributeLabelColumnClass]: !!context?.labelColumn && !isVertical.value,
    },
  ]);

  const dataClasses = computed(() => [
    attributeDataVariants[resolvedEmphasis.value],
    {
      [attributeVerticalProminentDataClass]:
        !isLegacy.value &&
        isVertical.value &&
        resolvedEmphasis.value === AttributeEmphasis.Prominent,
    },
    attributeToneClasses[props.tone],
  ]);

  const valueGroupClasses = computed(() => [
    attributeValueGroupClass,
    { [attributeLegacyClass]: isLegacy.value },
  ]);

  const detailClass = computed(() =>
    isVertical.value || isLegacy.value
      ? attributeDetailClasses.vertical
      : attributeDetailClasses.inline,
  );
</script>

<template>
  <div :class="rootClasses" :data-testid="resolvedTestId">
    <component
      :is="labelTag"
      :class="labelGroupClasses"
      :data-testid="`${resolvedTestId}-label`"
    >
      <slot name="label">
        <FontIcon
          v-if="icon && !isVertical"
          :icon-name="icon"
          :class="attributeIconClass"
          aria-hidden="true"
        />
        <span :class="attributeLabelClass">{{ label }}</span>
        <AttributeInfo
          v-if="info && !isVertical"
          :label="label"
          :info="info"
          :test-id="`${resolvedTestId}-info`"
        />
      </slot>
    </component>
    <component :is="valueTag" :class="attributeSectionClasses[resolvedLayout]">
      <div :class="valueGroupClasses">
        <div :class="dataClasses" :data-testid="`${resolvedTestId}-data`">
          <slot>{{ data || '-' }}</slot>
        </div>
        <span
          v-if="detail"
          :class="detailClass"
          :data-testid="`${resolvedTestId}-detail`"
          >{{
            detail
          }}</span
        >
      </div>
      <div
        v-if="$slots.action"
        :class="attributeActionClass"
        :data-testid="`${resolvedTestId}-action`"
      >
        <slot name="action" />
      </div>
    </component>
  </div>
</template>
